import AgTechTourHelpButton from "@agtech/web/components/App/Tour/AgTechTourHelpButton";
import { InvoiceChangeContactCreditsDialogTourConfig } from "./InvoiceChangeContactCreditsDialogTourConfig";
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import { AgTechFormReadOnlyTextControl } from "@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl";
import { AgTechFormRow } from "@agtech/web/components/Forms/Layout/AgTechFormRow";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected, markGridSelectionEntityAsUnselected } from "@agtech/web/components/Grids/AgTechGridEntities";
import { useCreditsDataWriteCreditInvoiceChangesAction, ChangeItemOnInvoiceDTO } from "@sales/data/lots/invoices/LotInvoiceDataActions";
import InvoiceChangeCreditsGrid from "../../components/grids/InvoiceChangeCreditsGrid";
import AgTechFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechFormDialog";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import { useInvoiceDataGetInvoiceDetailsQuery } from "@sales/data/invoicing/InvoicingDataQueries";
import SaleContactDetailPopUp from "../../../contacts/popups/detail/SaleContactDetailPopUp";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import { getContactFullName } from "@agtech/data/contacts/ContactData";
import ContactDialog from "@agtech/web/integrations/contacts/dialogs/ContactDialog";
import ContactCreditDialog from "../../../contacts/dialogs/credits/SaleContactCreditDialog";

export type InvoiceChangeContactCreditsDialogProps = {
    invoiceId: number,
    contactId: number
}

const InvoiceChangeContactCreditsDialog = (props: InvoiceChangeContactCreditsDialogProps) => {
    const dialogQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const dialogAction = useCreditsDataWriteCreditInvoiceChangesAction(props);
    
    return (
        <AgTechFormDialog
            width={650}
            dialogData={{
                query: dialogQuery,
                getDialogData: dialogData => {
                    const contactCredits = [
                        ...dialogData.invoice.details.creditsApplied,
                        ...Object.values(dialogData.contact.details.credits)
                    ];

                    return buildGridEntitySelectionIndex({
                        entities: contactCredits,
                        isSelected: c => dialogData.invoice.details.creditsApplied.some(d => d.id === c.id)
                    });
                },
                getDialogContext: dialogData => {
                    return {
                        invoice: dialogData.invoice,
                        contact: dialogData.contact
                    }
                }
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: (d, context) => {
                    const creditSelections = Object.values(d);
                    const updatedCredits = creditSelections.filter(d => d.isSelected != d.wasOriginallySelected);

                    return {
                        changes: updatedCredits.map<ChangeItemOnInvoiceDTO>(l => ({
                            remove: l.wasOriginallySelected == true && l.isSelected == false,
                            id: l.entity.id
                        })),
                        invoiceTimestamp: context.invoice.timestamp
                    };
                }
            }}
            structure={(data) => ({
                header: {
                    header: `Update Credits - Invoice #${data.context.invoice.info.number}`,
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={InvoiceChangeContactCreditsDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: 'Update Credits',
                width: 600
            })}
         >
            {(formContext) => {
                const creditSelections = Object.values(formContext.entity);
                const selectedCredits = creditSelections.filter(c => c.isSelected)
                    .map(credit => credit.entity);

                const originalCreditsTotal = formContext.context.invoice.details.creditsApplied.reduce((acc, curr) => acc + curr.creditAmount, 0);
                const updatedCreditsTotal = selectedCredits.reduce((acc, curr) => acc + curr!.creditAmount, 0);
                const InvoiceModifiersTotal = formContext.context.invoice.details.InvoiceModifiersGiven.reduce((acc, curr) => acc + curr.amount, 0);

                const lotsTotal = formContext.context.invoice.info.total + originalCreditsTotal + InvoiceModifiersTotal;

                return (
                    <AgTechFormBody>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption="Contact Name"
                                value={e => getContactFullName(formContext.context.contact.info)}
                                onClick={e => ContactDialog.Open(e, {
                                    contactId: formContext.context.contact.id
                                })}
                                showPopUpOnHover={e => ({
                                    popup: () => <SaleContactDetailPopUp contactId={formContext.context.contact.id} />
                                })}
                            />
                        </AgTechFormRow>
                        <div id='invoice-change-credits-summary'>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Lots sold'
                                value={e => AgTechMoneyFormatter.format(lotsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Charges and discounts Given'
                                value={e => AgTechMoneyFormatter.format(InvoiceModifiersTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Credits Applied'
                                value={e => AgTechMoneyFormatter.format(updatedCreditsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>=</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Invoice Amount'
                                value={e => AgTechMoneyFormatter.format(lotsTotal - updatedCreditsTotal - InvoiceModifiersTotal)}
                                classes='snug'
                            />
                        </AgTechFormRow>
                        </div>
                        <div id='invoice-change-credits-grid'>
                        <AgTechFormSection
                            header="Available Credits"
                            classes='flexed mt-2'
                            bodyClasses='flexed pt-3'
                            headerContent={() => (
                                <ContactCreditDialog.Button
                                    contactId={formContext.context.contact.id}
                                    classes='button-thin button-link !pr-0'
                                />
                            )}
                        >
                            <InvoiceChangeCreditsGrid
                                contactId={formContext.context.contact.id}
                                credits={formContext.entity}
                                onCreditSelected={async selectedCredit => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsSelected({
                                            selections: curr,
                                            entity: selectedCredit,
                                        });
                                    });
                                }}
                                onCreditDeselected={async deselectedCredit => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsUnselected({
                                            selections: curr,
                                            entity: deselectedCredit
                                        });
                                    });
                                }}
                            />
                        </AgTechFormSection>
                        </div>
                    </AgTechFormBody>
                )
            }}
        </AgTechFormDialog>
    )
}

InvoiceChangeContactCreditsDialog.Open = (e: AgTechClickEventHandler, props: InvoiceChangeContactCreditsDialogProps) => e.dialogs.openDialog({
    dialog: () => <InvoiceChangeContactCreditsDialog {...props} />
});

InvoiceChangeContactCreditsDialog.Button = (props: AgTechButtonProps & InvoiceChangeContactCreditsDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Change Credits',
        icon: 'bi bi-arrow-left-right',
        ...props,
        action: e => InvoiceChangeContactCreditsDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default InvoiceChangeContactCreditsDialog;